import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from './AuthContext';

const WorkspaceContext = createContext(null);

export const WorkspaceProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [folders, setFolders] = useState([]);
  const [unfiledWorkspaces, setUnfiledWorkspaces] = useState([]);
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchWorkspaces = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await api.get('/workspaces');
      const fetchedWorkspaces = response.data.workspaces || [];
      const fetchedFolders = response.data.folders || [];
      const fetchedUnfiled = response.data.unfiled_workspaces || [];
      
      setWorkspaces(fetchedWorkspaces);
      setFolders(fetchedFolders);
      setUnfiledWorkspaces(fetchedUnfiled);
      
      // Set current workspace
      if (fetchedWorkspaces.length > 0) {
        const savedWorkspaceId = localStorage.getItem('current_workspace_id') || user?.current_workspace_id;
        const savedWorkspace = fetchedWorkspaces.find(w => w.workspace_id === savedWorkspaceId);
        const selectedWorkspace = savedWorkspace || fetchedWorkspaces[0];
        setCurrentWorkspace(selectedWorkspace);
        localStorage.setItem('current_workspace_id', selectedWorkspace.workspace_id);
      } else {
        setCurrentWorkspace(null);
      }
    } catch (error) {
      console.error('Failed to fetch workspaces:', error);
      setCurrentWorkspace(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user?.current_workspace_id]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchWorkspaces();
    } else {
      setLoading(false);
      setWorkspaces([]);
      setFolders([]);
      setUnfiledWorkspaces([]);
      setCurrentWorkspace(null);
    }
  }, [isAuthenticated, fetchWorkspaces]);

  const switchWorkspace = useCallback((workspace) => {
    setCurrentWorkspace(workspace);
    localStorage.setItem('current_workspace_id', workspace.workspace_id);
  }, []);

  const createWorkspace = useCallback(async (data) => {
    const response = await api.post('/workspaces', data);
    const newWorkspace = response.data;
    await fetchWorkspaces();
    // Auto-switch to newly created workspace
    if (newWorkspace.workspace_id) {
      setCurrentWorkspace(newWorkspace);
      localStorage.setItem('current_workspace_id', newWorkspace.workspace_id);
    }
    return newWorkspace;
  }, [fetchWorkspaces]);

  const updateWorkspace = useCallback(async (workspaceId, data) => {
    await api.patch(`/workspaces/${workspaceId}`, data);
    await fetchWorkspaces();
  }, [fetchWorkspaces]);

  const inviteMember = useCallback(async (workspaceId, email, role = 'member') => {
    const response = await api.post(`/workspaces/${workspaceId}/invite`, { email, role });
    await fetchWorkspaces();
    return response.data;
  }, [fetchWorkspaces]);

  const removeMember = useCallback(async (workspaceId, userId) => {
    await api.delete(`/workspaces/${workspaceId}/members/${userId}`);
    await fetchWorkspaces();
  }, [fetchWorkspaces]);

  const leaveWorkspace = useCallback(async (workspaceId) => {
    await api.post(`/workspaces/${workspaceId}/leave`);
    await fetchWorkspaces();
    // Switch to another workspace if we left the current one
    if (currentWorkspace?.workspace_id === workspaceId && workspaces.length > 1) {
      const nextWorkspace = workspaces.find(w => w.workspace_id !== workspaceId);
      if (nextWorkspace) {
        switchWorkspace(nextWorkspace);
      }
    }
  }, [fetchWorkspaces, currentWorkspace, workspaces, switchWorkspace]);

  // Folder management functions
  const createFolder = useCallback(async (data) => {
    const response = await api.post('/folders', data);
    await fetchWorkspaces();
    return response.data;
  }, [fetchWorkspaces]);

  const updateFolder = useCallback(async (folderId, data) => {
    await api.patch(`/folders/${folderId}`, data);
    await fetchWorkspaces();
  }, [fetchWorkspaces]);

  const deleteFolder = useCallback(async (folderId) => {
    await api.delete(`/folders/${folderId}`);
    await fetchWorkspaces();
  }, [fetchWorkspaces]);

  const addWorkspaceToFolder = useCallback(async (folderId, workspaceId) => {
    await api.post(`/folders/${folderId}/workspaces/${workspaceId}`);
    await fetchWorkspaces();
  }, [fetchWorkspaces]);

  const removeWorkspaceFromFolder = useCallback(async (folderId, workspaceId) => {
    await api.delete(`/folders/${folderId}/workspaces/${workspaceId}`);
    await fetchWorkspaces();
  }, [fetchWorkspaces]);

  const value = {
    workspaces,
    folders,
    unfiledWorkspaces,
    currentWorkspace,
    loading,
    switchWorkspace,
    createWorkspace,
    updateWorkspace,
    inviteMember,
    removeMember,
    leaveWorkspace,
    refreshWorkspaces: fetchWorkspaces,
    // Folder functions
    createFolder,
    updateFolder,
    deleteFolder,
    addWorkspaceToFolder,
    removeWorkspaceFromFolder
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within WorkspaceProvider');
  }
  return context;
};

export default WorkspaceContext;
